<template>
  <div class="set-confirm-step pa-4">
    <div class="d-flex justify-space-between align-center mb-3">
      <v-card-subtitle class="pa-0 text-base">
        セット時間 / 料金の確認
      </v-card-subtitle>
      <v-btn
        small
        text
        color="primary"
        @click="toggleMode"
      >
        {{ isIndividual ? 'まとめて設定する' : '個別に設定する' }}
      </v-btn>
    </div>

    <!-- bulk モード -->
    <div
      v-if="!isIndividual"
      class="d-flex align-center py-2"
    >
      <div class="flex-grow-1">
        {{ customerCount }}名（まとめ）
      </div>
      <v-text-field
        :value="set.durationMin"
        type="number"
        hide-details
        dense
        outlined
        style="max-width: 80px;"
        @input="updateBulkDuration"
      />
      <span class="mx-1">分</span>
      <v-text-field
        :value="set.price"
        type="number"
        hide-details
        dense
        outlined
        style="max-width: 100px;"
        @input="updateBulkPrice"
      />
      <span class="mx-1">円</span>
      <v-btn-toggle
        :value="set.taxCharge || 'normal'"
        mandatory
        dense
        class="ml-3"
        @change="updateBulkTaxCharge"
      >
        <v-btn
          small
          value="normal"
        >
          通常
        </v-btn>
        <v-btn
          small
          value="cut"
        >
          カット
        </v-btn>
      </v-btn-toggle>
    </div>

    <!-- individual モード -->
    <div v-else>
      <set-editable-row
        v-for="(row, idx) in set.perCustomer"
        :key="`set-row-${idx}`"
        :label="`お客様${idx + 1}`"
        :duration-min="row.durationMin"
        :price="row.price"
        :tax-charge="row.taxCharge || 'normal'"
        @update:durationMin="updatePerCustomer(idx, { durationMin: $event })"
        @update:price="updatePerCustomer(idx, { price: $event })"
        @update:taxCharge="updatePerCustomer(idx, { taxCharge: $event })"
      />
    </div>

    <v-divider class="my-3" />

    <div class="d-flex flex-column align-end">
      <div class="text-h6">
        セット小計: {{ formatPrice(subtotal) }} 円
      </div>
      <div
        v-if="cutCount > 0"
        class="text-caption text--secondary"
      >
        ※ {{ cutCount }} 件は税サカット（{{ formatPrice(subtotal) }} 円に税サは加算されません）
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import SetEditableRow from '../parts/SetEditableRow.vue'

const buildPerCustomer = (count, base) => {
  const arr = []
  for (let i = 0; i < count; i += 1) {
    arr.push({
      durationMin: base.durationMin,
      price: base.price,
      taxCharge: base.taxCharge || 'normal',
    })
  }

  return arr
}

export default {
  name: 'SetConfirmStep',
  components: { SetEditableRow },
  props: {
    customerCount: { type: Number, required: true },
    selectedCourse: { type: Object, default: null },
    set: { type: Object, required: true },
  },
  emits: ['update:set'],
  setup(props, { emit }) {
    const isIndividual = computed(() => props.set.mode === 'individual')

    // セット小計: 既存仕様と同じく selling_price は taxCharge に関係なく合計に含める。
    // taxCharge: 'cut' は税サ（税+サービス料）が加算されないという意味で、本体価格は計上される。
    const subtotal = computed(() => {
      if (isIndividual.value) {
        return (props.set.perCustomer || [])
          .reduce((sum, r) => sum + Number(r.price || 0), 0)
      }

      return Number(props.set.price || 0) * Math.max(1, props.customerCount)
    })

    const cutCount = computed(() => {
      if (isIndividual.value) {
        return (props.set.perCustomer || [])
          .filter(r => (r.taxCharge || 'normal') === 'cut').length
      }

      return (props.set.taxCharge || 'normal') === 'cut'
        ? Math.max(1, props.customerCount)
        : 0
    })

    const formatPrice = n => Number(n || 0).toLocaleString('ja-JP')

    const emitSet = next => emit('update:set', next)

    const toggleMode = () => {
      if (isIndividual.value) {
        emitSet({ ...props.set, mode: 'bulk' })
      } else {
        emitSet({
          ...props.set,
          mode: 'individual',
          perCustomer: buildPerCustomer(props.customerCount, {
            durationMin: props.set.durationMin,
            price: props.set.price,
            taxCharge: props.set.taxCharge || 'normal',
          }),
        })
      }
    }

    const updateBulkDuration = v => emitSet({ ...props.set, durationMin: Number(v) || 0 })
    const updateBulkPrice = v => emitSet({ ...props.set, price: Number(v) || 0 })
    const updateBulkTaxCharge = v => emitSet({ ...props.set, taxCharge: v || 'normal' })

    const updatePerCustomer = (idx, patch) => {
      const next = (props.set.perCustomer || []).map((r, i) => (
        i === idx ? { ...r, ...patch } : r
      ))
      emitSet({ ...props.set, perCustomer: next })
    }

    return {
      isIndividual,
      subtotal,
      cutCount,
      formatPrice,
      toggleMode,
      updateBulkDuration,
      updateBulkPrice,
      updateBulkTaxCharge,
      updatePerCustomer,
    }
  },
}
</script>
